// Gráfico de velocidade ao longo do tempo para o histórico do veículo
// (Fase B). SVG puro — sem dependências. Mostra a velocidade como área,
// uma faixa de estado (condução / ralenti / parado) por baixo, e um
// cursor ao passar o rato com a hora + velocidade do ponto.

import React, { useState, useMemo } from 'react';

const COLORS = { driving: '#22c55e', idle: '#eab308', stopped: '#ef4444' };
const LABELS = { driving: 'Condução', idle: 'Ralenti', stopped: 'Parado' };

function stateOf(p) {
  const s = Number(p.speed) || 0;
  if (s > 3) return 'driving';
  if (p.ignition) return 'idle';
  return 'stopped';
}

// Valor da "bateria" de um ponto. O EcooGPS traça a TENSÃO de alimentação
// (attributes.power, V) — é o que varia num veículo. Só cai para a % da
// bateria interna (attributes.battery) quando não há power. Null se não houver.
function batteryOf(p) {
  const a = p.attributes || {};
  if (a.power != null && Number.isFinite(Number(a.power))) return Number(a.power);
  if (a.battery != null && Number.isFinite(Number(a.battery))) return Number(a.battery);
  if (a.batteryLevel != null && Number.isFinite(Number(a.batteryLevel))) return Number(a.batteryLevel);
  return null;
}
// RPM do motor (CAN) — igual ao EcooGPS: attributes.rpm.
function rpmOf(p) {
  const r = Number(p.attributes?.rpm);
  return Number.isFinite(r) && r >= 0 ? r : null;
}
// Nível de combustível (%) — igual ao EcooGPS (attributes.fuel, CAN-Bus);
// fallback aos IO analógicos que já usamos na popup (io89/io9).
function fuelOf(p) {
  const a = p.attributes || {};
  const raw = a.fuel != null ? a.fuel : (a.io89 != null ? a.io89 : a.io9);
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : null;
}

export default function VehicleHistoryChart({ points = [], cursorT = null, metric = 'speed' }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const isBattery = metric === 'battery';
  const isRpm = metric === 'rpm';
  const isFuel = metric === 'fuel';
  const isSpeed = !isBattery && !isRpm && !isFuel;
  const valueOf = isBattery ? batteryOf : isRpm ? rpmOf : isFuel ? fuelOf : (p) => Math.max(0, Number(p.speed) || 0);

  const data = useMemo(() => (
    (points || [])
      .filter(p => p && p.timestamp)
      .map(p => ({
        t: new Date(p.timestamp).getTime(),
        v: valueOf(p),
        state: stateOf(p),
      }))
      .filter(p => Number.isFinite(p.t) && (isSpeed || p.v != null))
      .sort((a, b) => a.t - b.t)
  ), [points, metric]); // eslint-disable-line react-hooks/exhaustive-deps

  const usesPower = useMemo(() => (points || []).some(p => p.attributes?.power != null), [points]);
  const val = (d) => d.v;
  const unit = isBattery ? (usesPower ? 'V' : '%') : isRpm ? 'RPM' : isFuel ? '%' : 'km/h';

  // Cursor externo (reprodução) — ponto mais próximo no tempo.
  const extIdx = useMemo(() => {
    if (cursorT == null || !data.length) return null;
    let best = 0, bestD = Infinity;
    data.forEach((d, i) => { const dd = Math.abs(d.t - cursorT); if (dd < bestD) { bestD = dd; best = i; } });
    return best;
  }, [cursorT, data]);

  if (data.length < 2) {
    return (
      <div style={{ padding: 14, textAlign: 'center', fontSize: '0.7rem', color: '#94a3b8' }}>
        {isBattery ? 'Sem dados de bateria para este período.'
          : isRpm ? 'Sem dados de RPM para este período.'
          : isFuel ? 'Sem dados de combustível para este período.'
          : 'Sem dados de velocidade suficientes para o gráfico.'}
      </div>
    );
  }

  const W = 1000, H = 150;
  const padL = 34, padR = 10, padT = 12, padB = 34;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const t0 = data[0].t;
  const t1 = data[data.length - 1].t;
  const span = Math.max(1, t1 - t0);
  // Escala Y: velocidade 0..max; bateria por tensão ajusta o eixo à variação
  // (min-max, senão a linha ~24V ficaria achatada no fundo); bateria % 0..100.
  let minV = 0, maxV;
  if (isBattery && usesPower) {
    const vals = data.map(d => d.v).filter(v => Number.isFinite(v));
    const lo = vals.length ? Math.min(...vals) : 0;
    const hi = vals.length ? Math.max(...vals) : 1;
    minV = Math.floor(lo - 0.5);
    maxV = Math.ceil(hi + 0.5);
    if (maxV - minV < 2) maxV = minV + 2;
  } else if (isBattery || isFuel) {
    minV = 0; maxV = 100; // tensão-% e combustível-% → 0..100
  } else if (isRpm) {
    minV = 0;
    maxV = Math.max(1000, Math.ceil(Math.max(...data.map(d => d.v)) / 500) * 500); // topo do eixo, como o EcooGPS
  } else {
    minV = 0;
    maxV = Math.max(60, Math.ceil(Math.max(...data.map(d => d.v)) / 20) * 20);
  }
  const xOf = (t) => padL + ((t - t0) / span) * plotW;
  const yOf = (s) => padT + plotH - ((s - minV) / (maxV - minV)) * plotH;
  // Cor da série por métrica (alinhado com o EcooGPS): velocidade azul,
  // bateria verde, RPM laranja, combustível vermelho.
  const stroke = isBattery ? '#16a34a' : isRpm ? '#d97706' : isFuel ? '#dc2626' : '#2563eb';
  const fill = isBattery ? '#22c55e' : isRpm ? '#f59e0b' : isFuel ? '#ef4444' : '#3b82f6';

  const lineD = data.map((d, i) => `${i ? 'L' : 'M'} ${xOf(d.t).toFixed(1)} ${yOf(val(d)).toFixed(1)}`).join(' ');
  const areaD = `M ${xOf(t0).toFixed(1)} ${padT + plotH} ${lineD.slice(2)} L ${xOf(t1).toFixed(1)} ${padT + plotH} Z`;

  // Faixas de estado — corridas contíguas do mesmo estado.
  const bands = [];
  let cur = { state: data[0].state, a: data[0].t };
  for (let i = 1; i < data.length; i++) {
    if (data[i].state !== cur.state) {
      cur.b = data[i].t; bands.push(cur);
      cur = { state: data[i].state, a: data[i].t };
    }
  }
  cur.b = t1; bands.push(cur);
  const bandY = padT + plotH + 4;
  const bandH = 7;

  const fmtT = (ms) => new Date(ms).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => t0 + f * span);

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    if (x < padL || x > W - padR) { setHoverIdx(null); return; }
    const t = t0 + ((x - padL) / plotW) * span;
    let best = 0, bestD = Infinity;
    data.forEach((d, i) => { const dd = Math.abs(d.t - t); if (dd < bestD) { bestD = dd; best = i; } });
    setHoverIdx(best);
  };

  // O hover do rato tem prioridade sobre o cursor da reprodução.
  const activeIdx = hoverIdx != null ? hoverIdx : extIdx;
  const hp = activeIdx != null ? data[activeIdx] : null;
  const hpFromPlayback = hoverIdx == null && extIdx != null;

  return (
    <div style={{ width: '100%' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block' }}
        onMouseMove={onMove} onMouseLeave={() => setHoverIdx(null)}>
        {[minV, (minV + maxV) / 2, maxV].map((s, i) => (
          <g key={i}>
            <line x1={padL} y1={yOf(s)} x2={W - padR} y2={yOf(s)} stroke="#e2e8f0" strokeWidth="1" />
            <text x={padL - 5} y={yOf(s) + 3} textAnchor="end" fontSize="9" fill="#94a3b8">{Math.round(s)}</text>
          </g>
        ))}
        <path d={areaD} fill={fill} fillOpacity="0.14" />
        <path d={lineD} fill="none" stroke={stroke} strokeWidth="1.6" />
        {bands.map((b, i) => (
          <rect key={i} x={xOf(b.a)} y={bandY} width={Math.max(1, xOf(b.b) - xOf(b.a))} height={bandH}
            fill={COLORS[b.state]} />
        ))}
        {ticks.map((t, i) => (
          <text key={i} x={xOf(t)} y={H - 6}
            textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'}
            fontSize="9" fill="#94a3b8">{fmtT(t)}</text>
        ))}
        {hp && (
          <g>
            <line x1={xOf(hp.t)} y1={padT} x2={xOf(hp.t)} y2={padT + plotH}
              stroke={hpFromPlayback ? '#2563eb' : '#0f172a'}
              strokeWidth={hpFromPlayback ? 1.6 : 1} strokeDasharray="3 2" />
            <circle cx={xOf(hp.t)} cy={yOf(val(hp))} r="3.5" fill={stroke} stroke="#fff" strokeWidth="1.5" />
          </g>
        )}
      </svg>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.6rem', color: '#64748b', padding: '1px 8px 0' }}>
        {Object.keys(LABELS).map(k => (
          <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: COLORS[k] }} />{LABELS[k]}
          </span>
        ))}
        {hp && (
          <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#0f172a' }}>
            {fmtT(hp.t)} · {Math.round(val(hp))} {unit} · {LABELS[hp.state]}
          </span>
        )}
      </div>
    </div>
  );
}
